import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, MapPin, LayoutGrid, X } from "lucide-react";
import { qs, useLoad, useTaxonomy } from "../lib/api";
import { catMeta } from "../lib/media";
import { Empty, ErrorBox, Loading, OrganicTag, StatusBadge } from "../components/kit";
import { Sponsored } from "../components/Sponsored";

export function ProviderCard({ v, to, areaName, testId, extra }: { v: any; to: string; areaName: (id: string) => string; testId: string; extra?: React.ReactNode }) {
  const m = catMeta(v.category_ids?.[0]);
  return (
    <li className="flex gap-4 rounded-lg border border-[var(--steel)] bg-white p-3 shadow-sm transition-shadow hover:shadow-md" data-testid={testId}>
      <img src={m.img} alt="" className="h-24 w-24 shrink-0 rounded-md object-cover sm:h-28 sm:w-32" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <p className="font-bold text-[var(--navy)]">{v.vendor_name ? `${v.vendor_name} — ` : ""}{v.trade_label}</p>
          <div className="flex flex-wrap gap-1"><StatusBadge value={v.availability} />{extra}</div>
        </div>
        <p className="text-xs text-[var(--muted)]">{(v.area_ids || []).map(areaName).join(" · ")}</p>
        <p className="mt-1 line-clamp-2 text-sm text-[#3b4660]">{v.headline || v.description}</p>
      </div>
      <div className="flex items-center"><Link to={to} className="btn btn-primary btn-sm" data-testid={`${testId}-view-details`}>View Details</Link></div>
    </li>
  );
}

function CategorySidebar({ cat, setCat, categories, counts }: any) {
  const items = [{ id: "", name: "All Services" }, ...categories];
  return (
    <nav aria-label="Category" className="h-fit min-w-0 rounded-lg border border-[var(--steel)] bg-white p-3">
      <p className="mb-2 px-2 font-bold">Category</p>
      <ul className="flex gap-1 overflow-x-auto lg:block lg:space-y-1">
        {items.map((c: any) => {
          const Icon = c.id ? catMeta(c.id).icon : LayoutGrid;
          return (
            <li key={c.id || "all"}>
              <button onClick={() => setCat(c.id)} aria-pressed={cat === c.id} className={`side-item ${cat === c.id ? "side-item-active" : ""}`} data-testid={`directory-category-${c.id || "all"}`}>
                <Icon className="h-4 w-4" aria-hidden /><span className="flex-1">{c.name}</span>
                {c.id && counts && <span className="mono text-xs text-[var(--muted)]" data-testid={`directory-count-${c.id}`}>{counts[c.id] || 0}</span>}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export default function Directory() {
  const [params, setParams] = useSearchParams();
  const { categories, areas, areaName } = useTaxonomy();
  const query = params.get("q") || "";
  const cat = params.get("category") || "";
  const area = params.get("area") || "";
  const [text, setText] = useState(query);
  useEffect(() => {
    setText(query);
  }, [query]);
  const setParam = (k: string, v: string) => {
    const n = new URLSearchParams(params);
    if (v) n.set(k, v); else n.delete(k);
    setParams(n);
  };
  const { data, loading, error, reload } = useLoad<any>(`/public/search?${qs({ q: query, category_id: cat, area_id: area })}`);
  const rows: any[] = data?.results || [];
  const matched: any[] | null = data?.matched_categories ?? null;
  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <h1 className="text-3xl font-extrabold text-[var(--navy)] sm:text-4xl">Find a Service</h1>
      <form className="search-bar mt-4" onSubmit={(e) => { e.preventDefault(); setParam("q", text.trim()); }} role="search">
        <label className="flex flex-1 items-center gap-2 px-3"><Search className="h-5 w-5 text-[var(--muted)]" aria-hidden /><span className="sr-only">What service do you need?</span>
          <input className="w-full py-3 outline-none" placeholder="What service do you need?" maxLength={80} value={text} onChange={(e) => setText(e.target.value)} data-testid="directory-search-input" />
        </label>
        <label className="flex items-center gap-2 border-t border-[var(--steel)] px-3 sm:border-l sm:border-t-0"><MapPin className="h-5 w-5 text-[var(--blue)]" aria-hidden /><span className="sr-only">Service area</span>
          <select className="w-full bg-transparent py-3 outline-none" value={area} onChange={(e) => setParam("area", e.target.value)} data-testid="directory-area-select">
            <option value="">All areas</option>{areas.map((a) => <option key={a.id} value={a.id}>{a.name}{data ? ` (${data.counts.by_area[a.id] || 0})` : ""}</option>)}
          </select>
        </label>
        <button className="btn btn-red m-1" data-testid="directory-search-button">Search</button>
      </form>
      {query && matched && (
        <p className="mt-3 flex flex-wrap items-center gap-2 text-sm" aria-live="polite" data-testid="directory-query-banner">
          {matched.length ? <>Showing <strong>{matched.map((c) => c.name).join(", ")}</strong> for “{query}”</> : <>No trade matches “{query}”. Try other words or pick a category.</>}
          <button className="btn btn-sm btn-ghost" onClick={() => setParam("q", "")} data-testid="directory-clear-query"><X className="h-3 w-3" aria-hidden />Clear search</button>
        </p>
      )}
      <div className="mt-6 grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
        <CategorySidebar cat={cat} setCat={(v: string) => setParam("category", v)} categories={categories} counts={data?.counts.by_category} />
        <div className="min-w-0 space-y-8">
          {!query && !cat && <section aria-labelledby="featured-h">
            <h2 id="featured-h" className="mb-3 text-lg font-bold">Featured Categories</h2>
            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
              {categories.map((c) => (
                <li key={c.id}>
                  <button onClick={() => setParam("category", c.id)} className={`tile ${cat === c.id ? "ring-2 ring-[var(--blue)]" : ""}`} data-testid={`directory-tile-${c.id}`}>
                    <img src={catMeta(c.id).img} alt="" className="h-24 w-full rounded-t-md object-cover" /><span className="block py-2 text-sm font-bold">{c.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>}
          <div className="grid gap-6 xl:grid-cols-[1fr_300px]">
            <section className="organic-zone" aria-labelledby="organic-heading" data-testid="directory-organic-section">
              <div className="mb-3 flex flex-wrap items-center gap-2"><OrganicTag text={data ? `${rows.length} approved provider${rows.length === 1 ? "" : "s"}` : "Approved providers"} />
                <h2 id="organic-heading" className="text-sm font-semibold text-[var(--muted)]">Names stay private until you engage · not influenced by ads or plans</h2></div>
              {loading && <Loading />}
              {error && <ErrorBox message={error} onRetry={reload} />}
              {data && !loading && rows.length === 0 && <Empty title="No approved providers match">Try another category, area or search.</Empty>}
              <ul className="space-y-3" data-testid="directory-results">{rows.map((v) => <ProviderCard key={v.ref} v={v} to={`/vendors/${v.ref}`} areaName={areaName} testId={`directory-vendor-${v.ref}`} />)}</ul>
            </section>
            <Sponsored placement="directory" categoryId={cat || undefined} />
          </div>
        </div>
      </div>
    </div>
  );
}
